import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { CustomField, FieldRenderConfig } from '../../../models/custom-fields.model';
import { CustomFieldRendererService } from '../../../services/custom-field-renderer.service';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Custom Field Renderer Component
 * Dynamically renders different types of form fields based on custom field configuration
 * Supports nested/dependent fields with recursive rendering
 * Follows the project's design system and Material Design principles
 */
@Component({
  selector: 'app-custom-field-renderer',
  templateUrl: './custom-field-renderer.component.html',
  styleUrls: ['./custom-field-renderer.component.scss'],
})
export class CustomFieldRendererComponent implements OnInit, OnDestroy, OnChanges {
  @Input() field!: CustomField;
  @Input() form!: FormGroup;
  @Input() disabled = false;

  private _destroy$ = new Subject<void>();
  fieldConfig!: FieldRenderConfig;
  currentChildField: CustomField | null = null;
  private previousValue: string | null = null;
  private childFieldCreationTimestamp: number = 0;

  constructor(
    private fieldRenderer: CustomFieldRendererService,
    private translateService: TranslocoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.fieldConfig = this.fieldRenderer.prepareFieldConfig(
      this.field,
      this.form.get(this.field.name)?.value,
      this.disabled,
    );

    // Handle disabled state through FormControl instead of template attribute
    this.updateDisabledState();

    // If field depends on another field, set up a dependency watcher
    this.setupDependencyWatcher();

    // Set up value change listener for select fields to handle child fields
    if (this.field.type === 'select') {
      this.setupSelectValueChangeListener();
      // Initialize child field if there's already a value
      const currentValue = this.form.get(this.field.name)?.value;
      if (currentValue) {
        this.updateChildField(currentValue);
      }
    }

    // Monitor for control removal (handles nested field cleanup when ancestor parents change)
    this.monitorControlExistence();

    // Trigger change detection to ensure the field is properly rendered
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && !changes['disabled'].firstChange) {
      this.updateDisabledState();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Watch parent field (if any) and toggle current field visibility/validation
   */
  private setupDependencyWatcher(): void {
    if (!this.field.depends_on) return;

    const parentControl = this.form.get(this.field.depends_on.field);
    if (!parentControl) return;

    // Apply initial state
    this.fieldRenderer.applyDependencyState(this.form, this.field);

    // React to changes
    parentControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.fieldRenderer.applyDependencyState(this.form, this.field);
      this.cdr.detectChanges();
    });
  }

  /**
   * Updates the disabled state of the form control
   */
  private updateDisabledState(): void {
    const control = this.formControl;
    if (control) {
      // Disable if explicitly disabled via @Input or if field is marked as read_only
      if (this.disabled || this.field.read_only) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }

  /**
   * Gets the form control for this field
   */
  get formControl() {
    return this.form.get(this.field.name);
  }

  /**
   * Gets CSS classes for the field
   */
  getFieldClasses(): string {
    return this.fieldRenderer.getFieldCssClasses(this.field);
  }

  /**
   * Should the field be rendered at all (based on dependency visibility)?
   */
  shouldRender(): boolean {
    if (!this.field?.depends_on) return true;
    return this.fieldRenderer.isDependencySatisfied(this.form, this.field);
  }

  /**
   * Gets Material form field appearance
   */
  getMatFieldAppearance(): string {
    return this.fieldRenderer.getMatFieldAppearance();
  }

  /**
   * Checks if current language is Arabic
   */
  isArabic(): boolean {
    return this.translateService.getActiveLang() === 'ar';
  }

  /**
   * Gets text direction based on language
   */
  getTextDirection(): string {
    return this.isArabic() ? 'rtl' : 'ltr';
  }

  /**
   * Handles date change for date/datetime fields
   */
  onDateChange(event: any): void {
    if (this.field.type === 'date' || this.field.type === 'datetime') {
      const value = event.target ? event.target.value : event.value;
      this.formControl?.setValue(value);
    }
  }

  /**
   * Handles input changes for real-time validation
   * Also enforces max value for number fields (handles paste operations)
   */
  onInputChange(event: any): void {
    let value = event.target ? event.target.value : event.value;

    // For number fields, enforce max value limit (handles paste operations)
    if (this.field.type === 'number' && value !== null && value !== undefined && value !== '') {
      const maxValue = this.getMaxValue();
      const minValue = this.getMinValue();

      const numValue = Number(value);

      if (!isNaN(numValue)) {
        // Check max value constraint
        if (maxValue !== undefined && numValue > maxValue) {
          value = maxValue.toString();
          if (event.target) {
            event.target.value = value;
          }
        }
        // Check min value constraint
        else if (minValue !== undefined && numValue < minValue) {
          value = minValue.toString();
          if (event.target) {
            event.target.value = value;
          }
        }
      }
    }

    this.formControl?.setValue(value);

    // Trigger validation immediately
    this.formControl?.markAsTouched();
    this.formControl?.updateValueAndValidity();
  }

  /**
   * Restricts input to numeric characters only and enforces max value limit
   */
  onNumberKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);

    // Allow control keys (backspace, delete, tab, escape, enter, etc.)
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'Tab' ||
      event.key === 'Escape' ||
      event.key === 'Enter' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Home' ||
      event.key === 'End' ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) ||
      // Allow Cmd+A, Cmd+C, Cmd+V, Cmd+X (Mac)
      (event.metaKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
    ) {
      return true;
    }

    // Get current input state
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;
    const selectionEnd = inputElement.selectionEnd || 0;

    // Allow negative sign only at the start
    if (charStr === '-' && currentValue.length === 0) {
      return true;
    }

    // Allow decimal point only once
    if (charStr === '.' && !currentValue.includes('.')) {
      return true;
    }

    // Only allow numeric digits
    if (charStr < '0' || charStr > '9') {
      event.preventDefault();
      return false;
    }

    // Check if adding this digit would exceed max value
    const maxValue = this.getMaxValue();
    if (maxValue !== undefined) {
      // Build the prospective value after this keystroke
      const beforeSelection = currentValue.substring(0, selectionStart);
      const afterSelection = currentValue.substring(selectionEnd);
      const prospectiveValue = beforeSelection + charStr + afterSelection;

      // Convert to number and check against max
      const prospectiveNumber = Number(prospectiveValue);

      // If the prospective value is a valid number and exceeds max, prevent input
      if (!isNaN(prospectiveNumber) && prospectiveNumber > maxValue) {
        event.preventDefault();
        return false;
      }
    }

    return true;
  }

  /**
   * Handles blur event on number inputs - validates and cleans up the input value
   */
  onNumberInputBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (input) {
      // Validate and clean up the input value
      this.cleanupNumberInput(input);
    }
  }

  /**
   * Cleans up and validates number input value
   */
  private cleanupNumberInput(input: HTMLInputElement): void {
    let value = input.value;

    // Remove any non-numeric characters except decimal point and negative sign
    value = value.replace(/[^0-9.-]/g, '');

    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Ensure negative sign is only at the start
    if (value.includes('-')) {
      const isNegative = value.startsWith('-');
      value = value.replace(/-/g, '');
      if (isNegative) {
        value = '-' + value;
      }
    }

    // Update the form control if value changed
    if (input.value !== value) {
      input.value = value;
      this.formControl?.setValue(value);
    }
  }

  /**
   * Gets the maxlength attribute for input fields based on validation rules
   */
  getMaxLength(): number | undefined {
    if (this.field.validations && this.field.validations[this.field.name]) {
      const maxRule = this.field.validations[this.field.name].find((rule) =>
        rule.startsWith('max:'),
      );
      if (maxRule) {
        return Number(maxRule.split(':')[1]);
      }
    }
    return undefined;
  }

  /**
   * Gets the max value for number fields based on validation rules
   */
  private getMaxValue(): number | undefined {
    if (this.field.validations && this.field.validations[this.field.name]) {
      const maxRule = this.field.validations[this.field.name].find((rule) =>
        rule.startsWith('max:'),
      );
      if (maxRule && this.field.type === 'number') {
        return Number(maxRule.split(':')[1]);
      }
    }
    return undefined;
  }

  /**
   * Gets the min value for number fields based on validation rules
   */
  private getMinValue(): number | undefined {
    if (this.field.validations && this.field.validations[this.field.name]) {
      const minRule = this.field.validations[this.field.name].find((rule) =>
        rule.startsWith('min:'),
      );
      if (minRule && this.field.type === 'number') {
        return Number(minRule.split(':')[1]);
      }
    }
    return undefined;
  }

  /**
   * Gets the current character count for the field
   */
  getCurrentCharCount(): number {
    const control = this.formControl;
    return control?.value?.toString().length || 0;
  }

  /**
   * Checks if the field is approaching the character limit (80% of max)
   */
  isApproachingLimit(): boolean {
    const maxLength = this.getMaxLength();
    const currentCount = this.getCurrentCharCount();
    if (!maxLength) return false;

    return currentCount >= maxLength * 0.8 && currentCount < maxLength;
  }

  /**
   * Checks if the field has reached the character limit
   */
  hasReachedLimit(): boolean {
    const maxLength = this.getMaxLength();
    const currentCount = this.getCurrentCharCount();
    if (!maxLength) return false;

    return currentCount >= maxLength;
  }

  /**
   * Gets the character count message
   */
  getCharCountMessage(): string {
    const maxLength = this.getMaxLength();
    const currentCount = this.getCurrentCharCount();

    if (!maxLength) return '';

    if (this.hasReachedLimit()) {
      return this.translateService.translate('validation.char_count.max_reached', {
        current: currentCount,
        max: maxLength,
      });
    } else if (this.isApproachingLimit()) {
      return this.translateService.translate('validation.char_count.approaching', {
        current: currentCount,
        max: maxLength,
      });
    } else {
      return this.translateService.translate('validation.char_count.normal', {
        current: currentCount,
        max: maxLength,
      });
    }
  }

  /**
   * Formats date value for display
   */
  formatDateValue(value: any): string {
    if (this.field.type === 'date') {
      return this.fieldRenderer.formatDateValue(value, 'date');
    } else if (this.field.type === 'datetime') {
      return this.fieldRenderer.formatDateValue(value, 'datetime');
    }
    return value;
  }

  /**
   * Gets the input type for HTML input elements
   */
  getInputType(): string {
    switch (this.field.type) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'datetime':
        return 'datetime-local';
      case 'text':
      default:
        return 'text';
    }
  }

  /**
   * Gets placeholder text with translation
   */
  getPlaceholder(): string {
    return this.field.placeholder || '';
  }

  /**
   * Gets label text with translation
   */
  getLabel(): string {
    return this.field.label || '';
  }

  /**
   * Checks if field has validation errors
   */
  hasError(): boolean {
    const control = this.formControl;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Gets error message for the field
   */
  getErrorMessage(): string {
    const control = this.formControl;
    if (!control || !control.errors) return '';

    if (control.errors['required']) {
      return this.translateService.translate('validation.required', { field: this.getLabel() });
    }
    if (control.errors['pattern']) {
      return this.translateService.translate('validation.pattern', { field: this.getLabel() });
    }
    if (control.errors['minlength']) {
      const minLength = control.errors['minlength'].requiredLength;
      return this.translateService.translate('validation.minlength', {
        field: this.getLabel(),
        min: minLength,
      });
    }
    if (control.errors['maxlength']) {
      const maxLength = control.errors['maxlength'].requiredLength;
      return this.translateService.translate('validation.maxlength', {
        field: this.getLabel(),
        max: maxLength,
      });
    }
    if (control.errors['min']) {
      const minValue = control.errors['min'].min;
      return this.translateService.translate('validation.min', {
        field: this.getLabel(),
        min: minValue,
      });
    }
    if (control.errors['max']) {
      const maxValue = control.errors['max'].max;
      return this.translateService.translate('validation.max', {
        field: this.getLabel(),
        max: maxValue,
      });
    }

    return this.translateService.translate('validation.invalid');
  }

  /**
   * Checks if an option should be disabled based on its status.
   */
  isOptionDisabled(option: any): boolean {
    const normalizedStatus = String(option?.status ?? '')
      .trim()
      .toLowerCase();

    if (!normalizedStatus) {
      return false;
    }

    return !['approve', 'approved'].includes(normalizedStatus);
  }

  /**
   * Tracks option items for select fields
   */
  trackByOption(index: number, option: any): any {
    return option.value;
  }

  /**
   * Tracks child field to force component recreation when field changes
   * Returns a unique identifier that includes a creation timestamp
   * This ensures Angular ALWAYS recreates the component, even when going back to the same parent value
   */
  trackByChildField = (index: number, field: CustomField | null): string => {
    if (!field) return 'null';

    // Use the creation timestamp to ensure uniqueness even when going back to the same parent value
    // This forces Angular to always create a fresh component instance
    const uniqueKey = `${field.name}_${this.childFieldCreationTimestamp}`;

    return uniqueKey;
  };

  /**
   * Checks if the field is required
   */
  isRequired(): boolean {
    return this.field.required;
  }

  /**
   * Sets up value change listener for select fields to handle child field rendering
   */
  private setupSelectValueChangeListener(): void {
    const control = this.formControl;
    if (!control) return;

    control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.handleSelectChange(value);
    });
  }

  /**
   * Monitors the existence of this field's control in the form
   * Recursively clears child field references when this control is removed
   * This handles cases where ancestor parent fields change and remove nested descendants
   */
  private monitorControlExistence(): void {
    // Only monitor if this is a select field with potential children
    if (this.field.type !== 'select') return;

    // Subscribe to form value changes to detect when controls are removed
    this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      // Use setTimeout to check after form updates have completed
      setTimeout(() => {
        if (this._destroy$.closed) return; // Component was destroyed

        // Only check if we currently have a child field reference
        if (this.currentChildField) {
          const childControlExists = this.form.contains(this.currentChildField.name);
          const parentValue = this.form.get(this.field.name)?.value;
          const expectedChild = this.fieldRenderer.findChildField(this.field, parentValue);

          // Clear child field if:
          // 1. The control doesn't exist AND
          // 2. Either no child is expected, or the expected child is different
          if (
            !childControlExists &&
            (!expectedChild || expectedChild.name !== this.currentChildField.name)
          ) {
            this.clearChildFieldRecursively();
            this.cdr.detectChanges();
          }
        }
      }, 0);
    });
  }

  /**
   * Recursively clears the current child field and triggers re-validation
   * This ensures that when a parent at any level changes, all descendants are properly cleaned up
   */
  private clearChildFieldRecursively(): void {
    if (this.currentChildField) {
      this.currentChildField = null;
    }
  }

  /**
   * Handles select field value change
   * @param newValue - The new selected value
   */
  private handleSelectChange(newValue: string): void {
    // Update child field display
    this.updateChildField(newValue);

    // Handle form control changes (add/remove child fields)
    this.fieldRenderer.handleParentFieldChange(
      this.form,
      this.field,
      newValue,
      this.previousValue || undefined,
    );

    // Update previous value
    this.previousValue = newValue;

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Updates the current child field based on the selected value
   * @param selectedValue - The selected value
   */
  private updateChildField(selectedValue: string): void {
    // Check for null, undefined, or empty string
    if (
      !selectedValue ||
      selectedValue === '' ||
      selectedValue === null ||
      selectedValue === undefined
    ) {
      this.currentChildField = null;
      // Update timestamp to force recreation if user selects a value again
      this.childFieldCreationTimestamp = Date.now();
      return;
    }

    // Find and set the child field based on the selected value
    // The template will check if the control exists before rendering
    const newChildField = this.fieldRenderer.findChildField(this.field, selectedValue);

    // Update timestamp EVERY time we set a new child field
    // This ensures Angular always creates a fresh component, even when going back to the same parent value
    this.childFieldCreationTimestamp = Date.now();
    this.currentChildField = newChildField;
  }

  /**
   * Checks if the current field has a child field for the current value
   */
  hasChildField(): boolean {
    // Simple check - just see if we have a child field reference
    // The template will verify the control exists before rendering
    return this.currentChildField !== null;
  }

  /**
   * Gets the current child field
   */
  getChildField(): CustomField | null {
    return this.currentChildField;
  }
}
